import { Close } from "@mui/icons-material";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { CustomButton } from "src/Components/Common/Button/CustomButton";
import { DIALOG_MAX_WIDTHS } from "src/Components/Common/Modal/ModalWidth";
import { TagsInputUniqueKeys } from "../../ReconMIS/AutoCompletes";

import { FixedSizeList, ListChildComponentProps } from "react-window";
import { VendorDisputeStatusEnumDR } from "../../DisputeResolution/DisputedEntries/constants/disputeEntries";

const LISTBOX_PADDING = 8; // px

function renderRow(props: ListChildComponentProps) {
  const { data, index, style } = props;
  const option = data[index];

  const mergedStyle = {
    ...option.props.style, // ✅ preserves color/fontWeight from renderOption
    ...style, // ✅ includes positioning from react-window
    top: (style.top as number) + LISTBOX_PADDING,
  };

  return React.cloneElement(option, {
    ...option.props,
    style: mergedStyle,
  });
}

export const VirtualizedListboxComponent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLElement>>(
  function ListboxComponent(props, ref) {
    const { children, ...other } = props;
    const items = React.Children.toArray(children);
    const ITEM_SIZE = 40;
    const height = Math.min(8, items.length) * ITEM_SIZE + 2 * LISTBOX_PADDING;
    const OuterElementType = React.forwardRef<HTMLDivElement>((props, ref) => <div ref={ref} {...props} {...other} />);
    OuterElementType.displayName = "OuterElementType";
    return (
      <div ref={ref}>
        <FixedSizeList
          height={height}
          itemCount={items.length}
          itemSize={ITEM_SIZE}
          width="100%"
          itemData={items}
          outerElementType={OuterElementType}
          innerElementType="ul"
        >
          {renderRow}
        </FixedSizeList>
      </div>
    );
  }
);

export function useDebounce<T>(value: T, delay = 300): T {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(timer); // cleanup on new keystroke
  }, [value, delay]);

  return debouncedValue;
}

const EXCLUDE = "Exclude";

const VpFilterOfDisputedEntries = ({
  openFilterOfDisputedEntries,
  setOpenFilterOfDisputedEntries,
  filters,
  setFilters,
  data,
}) => {
  const getFilterValue = (filters: any[], id: string): string[] => {
    const filter = filters.find((f) => f.id === id);
    return Array.isArray(filter?.value) ? filter.value.filter((v) => v !== null && v !== undefined && v !== "") : [];
  };

  const [vendorNames, setVendorNames] = useState<string[]>(getFilterValue(filters, "vendorName"));
  const [vendorPan, setVendorPan] = useState<string[]>(getFilterValue(filters, "vendorPan"));
  const [instamartGrnNumber, setInstamartGrnNumber] = useState<string[]>(getFilterValue(filters, "instamartGrnNumber"));
  const [vendorInvoiceNumber, setVendorInvoiceNumber] = useState<string[]>(
    getFilterValue(filters, "vendorInvoiceNumber")
  );
  const [commentsFilter, setCommentsFilter] = useState<string[]>(getFilterValue(filters, "newComments"));
  // const [issueRaised, setIssueRaised] = useState<string[]>(getFilterValue(filters, "issueRaised"));
  const [instamartDnNumber, setInstamartDNNumber] = useState<string[]>(getFilterValue(filters, "instamartDnNumber"));
  const [vendorCnNumber, setVendorCNNumber] = useState<string[]>(getFilterValue(filters, "vendorCnNumber"));
  const [taskId, setTaskId] = useState<string[]>(getFilterValue(filters, "taskId"));
  const [disputeStatus, setDisputeStatus] = useState<string[]>(getFilterValue(filters, "vendorDisputeStatus"));

  //  performance optimization

  const [invoiceInput, setInvoiceInput] = useState("");
  const [instamartDNInput, setInstamartDNInput] = useState("");
  const [vendorCNInput, setVendorCNInput] = useState("");

  const debouncedInvoiceInput = useDebounce(invoiceInput, 200);
  const debouncedInstamartDNInput = useDebounce(instamartDNInput, 200);
  const debouncedVendorCNInput = useDebounce(vendorCNInput, 200);

  const handleClose = () => {
    setOpenFilterOfDisputedEntries(false);
  };

  const handleApplyFilter = () => {
    const newFilters = [];

    if (vendorNames.length > 0) {
      newFilters.push({ id: "vendorName", value: vendorNames });
    }

    if (vendorPan.length > 0) {
      newFilters.push({ id: "vendorPan", value: vendorPan });
    }

    if (instamartGrnNumber.length > 0) {
      newFilters.push({ id: "instamartGrnNumber", value: instamartGrnNumber });
    }

    if (vendorInvoiceNumber.length > 0) {
      newFilters.push({ id: "vendorInvoiceNumber", value: vendorInvoiceNumber });
    }
    if (instamartDnNumber.length > 0) {
      newFilters.push({ id: "instamartDnNumber", value: instamartDnNumber });
    }
    if (vendorCnNumber.length > 0) {
      newFilters.push({ id: "vendorCnNumber", value: vendorCnNumber });
    }
    if (taskId.length > 0) {
      newFilters.push({ id: "taskId", value: taskId });
    }

    if (disputeStatus.length > 0) {
      newFilters.push({ id: "vendorDisputeStatus", value: disputeStatus });
    }

    if (commentsFilter[0] === "Unread") {
      newFilters.push({ id: "newComments", value: ["Unread"] });
    } else if (commentsFilter[0] === "Read") {
      newFilters.push({ id: "newComments", value: ["Read"] });
    }

    // commentsFilter[0] === "All" => skip filter

    // Apply filters to MRT
    setFilters((prevFilters) => [...prevFilters, ...newFilters]);
    handleClose();
  };

  const handleClearFilters = () => {
    setFilters([]);
    setVendorNames([]);
    setVendorPan([]);
    setInstamartGrnNumber([]);
    setVendorInvoiceNumber([]);
    setCommentsFilter(["All"]);
    setInstamartDNNumber([]);
    setVendorCNNumber([]);
    setTaskId([]);
    setDisputeStatus([]);

    handleClose();
  };

  function extractUniqueOptions<T extends string>(data: T[], field: string): { [key: string]: T }[] {
    // const values = [EXCLUDE, ...new Set(data.filter((val) => typeof val === "string" && val.trim() !== ""))];
    const values = [...new Set(data.filter((val) => typeof val === "string" && val.trim() !== ""))];
    return values.map((val) => ({ [field]: val } as { [key: string]: T }));
  }

  const allInvoiceOptions = useMemo(() => {
    const allInvoiceOptionsValues = data
      .map((item) => item.vendorInvoiceNumber)
      .filter((val) => typeof val === "string" && val.trim() !== "");
    return extractUniqueOptions<string>(allInvoiceOptionsValues, "vendorInvoiceNumber");
  }, [data]);

  const allInstamartDNOptions = useMemo(() => {
    const allInstamartDNOptionsValues = data
      .map((item) => item.instamartDnNumber)
      .filter((val) => typeof val === "string" && val.trim() !== "");
    return extractUniqueOptions<string>(allInstamartDNOptionsValues, "instamartDnNumber");
  }, [data]);

  const allVendorCNOptions = useMemo(() => {
    const allVendorCNOptionsValues = data
      .map((item) => item.vendorCnNumber)
      .filter((val) => typeof val === "string" && val.trim() !== "");
    return extractUniqueOptions<string>(allVendorCNOptionsValues, "vendorCnNumber");
  }, [data]);

  // Dispute Status options from enum
  const allDisputeStatusOptions = useMemo(() => {
    return [
      { vendorDisputeStatus: VendorDisputeStatusEnumDR.NO_DISPUTE },
      { vendorDisputeStatus: VendorDisputeStatusEnumDR.DISPUTE_RAISED },
      { vendorDisputeStatus: VendorDisputeStatusEnumDR.DISPUTE_RESOLVED },
    ];
  }, []);

  const instamartDNNumberOptions = useMemo(() => {
    if (!debouncedInstamartDNInput) return allInstamartDNOptions.slice(0, 100);
    return allInstamartDNOptions.filter((opt) =>
      opt.instamartDnNumber.toLowerCase().includes(debouncedInstamartDNInput.toLowerCase())
    );
  }, [allInstamartDNOptions, debouncedInstamartDNInput]);

  const vendorCNNumberOptions = useMemo(() => {
    if (!debouncedVendorCNInput) return allVendorCNOptions.slice(0, 100);
    return allVendorCNOptions.filter((opt) =>
      opt.vendorCnNumber.toLowerCase().includes(debouncedVendorCNInput.toLowerCase())
    );
  }, [allVendorCNOptions, debouncedVendorCNInput]);

  const invoiceNumberOptions = useMemo(() => {
    if (!debouncedInvoiceInput) return allInvoiceOptions.slice(0, 100);
    return allInvoiceOptions.filter((opt) =>
      opt.vendorInvoiceNumber.toLowerCase().includes(debouncedInvoiceInput.toLowerCase())
    );
  }, [allInvoiceOptions, debouncedInvoiceInput]);

  return (
    <div>
      <Dialog open={openFilterOfDisputedEntries} maxWidth={DIALOG_MAX_WIDTHS.SM} fullWidth>
        <DialogTitle className="dialog-title">
          <div className="heading-m">Filter</div>
          <div className="vertical_center_align">
            <IconButton onClick={handleClose}>
              <Close />
            </IconButton>
          </div>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 2 }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "8px 16px",
            }}
          >
            <Typography variant="subtitle2">Vendor Invoice Number</Typography>
            <Box sx={{ width: "350px" }}>
              <TagsInputUniqueKeys
                tags={vendorInvoiceNumber}
                setTags={setVendorInvoiceNumber}
                className="partnerNamesAutocomplete"
                fullWidth={true}
                label=""
                placeholder="Select Invoice Number"
                splitter="newline"
                limitTags={2}
                textFieldProps={{ multiline: true, maxRows: 4 }}
                options={invoiceNumberOptions}
                renderOption={(props, option) => (
                  <li
                    {...props}
                    style={option.vendorInvoiceNumber === EXCLUDE ? { color: "orange", fontWeight: 600 } : {}}
                  >
                    {option.vendorInvoiceNumber}
                  </li>
                )}
                accessor={"vendorInvoiceNumber"}
                uniqueKeyAccessor={"vendorInvoiceNumber"}
                ListboxComponent={VirtualizedListboxComponent} // ✅ Add virtualization here
                inputValue={invoiceInput}
                onInputChange={(_, val) => setInvoiceInput?.(val)}
              />
            </Box>
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "8px 16px",
            }}
          >
            <Typography variant="subtitle2">Instamart DN Number</Typography>
            <Box sx={{ width: "350px" }}>
              <TagsInputUniqueKeys
                tags={instamartDnNumber}
                setTags={setInstamartDNNumber}
                className="partnerNamesAutocomplete"
                fullWidth={true}
                label=""
                placeholder="Select Instamart DN Number"
                splitter="newline"
                limitTags={2}
                textFieldProps={{ multiline: true, maxRows: 4 }}
                options={instamartDNNumberOptions}
                renderOption={(props, option) => (
                  <li
                    {...props}
                    style={option.instamartDnNumber === EXCLUDE ? { color: "orange", fontWeight: 600 } : {}}
                  >
                    {option.instamartDnNumber}
                  </li>
                )}
                accessor={"instamartDnNumber"}
                uniqueKeyAccessor={"instamartDnNumber"}
                ListboxComponent={VirtualizedListboxComponent} // ✅ Add virtualization here
                inputValue={instamartDNInput}
                onInputChange={(_, val) => setInstamartDNInput?.(val)}
              />
            </Box>
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "8px 16px",
            }}
          >
            <Typography variant="subtitle2">Vendor CN Number</Typography>
            <Box sx={{ width: "350px" }}>
              <TagsInputUniqueKeys
                tags={vendorCnNumber}
                setTags={setVendorCNNumber}
                className="partnerNamesAutocomplete"
                fullWidth={true}
                label=""
                placeholder="Select CN Number"
                splitter="newline"
                limitTags={2}
                textFieldProps={{ multiline: true, maxRows: 4 }}
                options={vendorCNNumberOptions}
                renderOption={(props, option) => (
                  <li {...props} style={option.vendorCnNumber === EXCLUDE ? { color: "orange", fontWeight: 600 } : {}}>
                    {option.vendorCnNumber}
                  </li>
                )}
                accessor={"vendorCnNumber"}
                uniqueKeyAccessor={"vendorCnNumber"}
                ListboxComponent={VirtualizedListboxComponent} // ✅ Add virtualization here
                inputValue={vendorCNInput}
                onInputChange={(_, val) => setVendorCNInput?.(val)}
              />
            </Box>
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "8px 16px",
            }}
          >
            <Typography variant="subtitle2">Dispute Status</Typography>
            <Box sx={{ width: "350px" }}>
              <TagsInputUniqueKeys
                tags={disputeStatus}
                setTags={setDisputeStatus}
                className="partnerNamesAutocomplete"
                fullWidth={true}
                label=""
                placeholder="Select Dispute Status"
                splitter="newline"
                limitTags={2}
                textFieldProps={{ multiline: true, maxRows: 4 }}
                options={allDisputeStatusOptions}
                renderOption={(props, option) => <li {...props}>{option.vendorDisputeStatus}</li>}
                accessor={"vendorDisputeStatus"}
                uniqueKeyAccessor={"vendorDisputeStatus"}
                ListboxComponent={VirtualizedListboxComponent}
              />
            </Box>
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "8px 16px",
            }}
          >
            <Typography variant="subtitle2">Vendor Remarks</Typography>
            <Box sx={{ width: "375px" }}>
              <RadioGroup
                className="space_between"
                row
                defaultValue="All" // Default selected value
                value={commentsFilter[0]} // Controlled value
                onChange={(e) => {
                  console.log(e.target.value); // Handle the selected value (all, unread, or read)
                  setCommentsFilter([e.target.value]); // Update the state with the selected value
                }}
              >
                <FormControlLabel value="All" control={<Radio size="small" />} label="All" />
                <FormControlLabel value="Unread" control={<Radio size="small" />} label="Unread" />
                <FormControlLabel value="Read" control={<Radio size="small" />} label="Read" />
              </RadioGroup>
            </Box>
          </Box>

          {/* <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "8px 16px",
            }}
          >
            <Typography variant="subtitle2">Issue raised</Typography>
            <Box sx={{ width: "375px" }}>
              <RadioGroup
                className="space_between"
                row
                defaultValue="All" // Default selected value
                value={issueRaised[0]} // Controlled value
                onChange={(e) => {
                  console.log(e.target.value); // Handle the selected value (all, unread, or read)
                  setIssueRaised([e.target.value]); // Update the state with the selected value
                }}
              >
                <FormControlLabel value="All" control={<Radio size="small" />} label="All" />
                <FormControlLabel value="Pending" control={<Radio size="small" />} label="Pending" />
                <FormControlLabel value="Updated" control={<Radio size="small" />} label="Updated by Instamart" />
              </RadioGroup>
            </Box>
          </Box> */}
        </DialogContent>
        <DialogActions className="dialog-actions">
          <CustomButton size="medium" colorToken="violet" variant="text" onClick={handleClearFilters}>
            Clear Filter
          </CustomButton>
          <CustomButton
            size="medium"
            colorToken="violet"
            variant="contained"
            //    disabled={isEditing}
            //    startIcon={<LoadingIcon loading={isEditing} />}
            onClick={() => {
              handleApplyFilter();
            }}
          >
            Apply
          </CustomButton>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default VpFilterOfDisputedEntries;
